import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useHasPermission } from '@/auth/auth-store';
import { useCreateScreenFlow, useScreenFlows } from '@/hooks/use-screen-flows';
import { useT } from '@/i18n/use-t';
import type { ScreenSurface } from './screen-flow-types';

function extractError(err: unknown): string {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string } | undefined;
    return body?.message ?? err.message;
  }
  return 'Unexpected error';
}

/**
 * RFL-142 — journey-flow list. Each flow is a per-surface screen journey
 * (start → … → end) authored in the graphical editor.
 */
export function ScreenFlowsListPage() {
  const { data, isLoading } = useScreenFlows();
  const create = useCreateScreenFlow();
  const navigate = useNavigate();
  const canWrite = useHasPermission('screen-flows.write');
  const t = useT('admin_ui');

  const [surface, setSurface] = useState<ScreenSurface>('sco');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onCreate(): Promise<void> {
    setError(null);
    if (name.trim().length === 0) {
      setError(t('screenFlows.list.nameRequired', 'Name is required.'));
      return;
    }
    try {
      const flow = await create.mutateAsync({ surface, name: name.trim() });
      navigate(`/screen-flows/${flow.id}`);
    } catch (err) {
      setError(extractError(err));
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('screenFlows.title', 'Screen flows')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'screenFlows.list.intro',
            'Design the terminal screen journey as a graph — start screen → triggers → branches → payment. Published journeys sync to terminals.',
          )}
        </p>
      </header>

      {canWrite && (
        <section className="flex flex-wrap items-end gap-3 rounded-card border border-border bg-card p-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('screenFlows.surface', 'Surface')}
            </label>
            <Select
              className="w-40"
              value={surface}
              onChange={(e) => setSurface(e.target.value as ScreenSurface)}
              aria-label={t('screenFlows.surface', 'Surface')}
            >
              <option value="sco">SCO</option>
              <option value="pos">POS</option>
              <option value="attendant">Attendant</option>
            </Select>
          </div>
          <div className="flex-1 space-y-1" style={{ minWidth: 240 }}>
            <label className="text-xs text-muted-foreground">{t('screenFlows.name', 'Name')}</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('screenFlows.list.namePlaceholder', 'e.g. SCO express journey')}
              maxLength={200}
            />
          </div>
          <Button onClick={onCreate} disabled={create.isPending}>
            {create.isPending
              ? t('screenFlows.list.creating', 'Creating…')
              : t('screenFlows.list.create', 'New journey')}
          </Button>
        </section>
      )}

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      ) : data === undefined || data.flows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('screenFlows.list.empty', 'No journeys yet.')}
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>{t('screenFlows.name', 'Name')}</TH>
              <TH>{t('screenFlows.surface', 'Surface')}</TH>
              <TH>{t('screenFlows.status', 'Status')}</TH>
              <TH className="text-right">{t('screenFlows.nodes', 'Nodes')}</TH>
              <TH className="text-right">{t('screenFlows.version', 'Version')}</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {data.flows.map((f) => (
              <TR key={f.id}>
                <TD>{f.name}</TD>
                <TD>
                  <Badge variant="outline">{f.surface}</Badge>
                </TD>
                <TD>
                  <Badge variant={f.status === 'published' ? 'success' : 'default'}>
                    {f.status}
                  </Badge>
                </TD>
                <TD className="text-right font-mono text-xs">{f.graph.nodes.length}</TD>
                <TD className="text-right font-mono text-xs">v{f.version}</TD>
                <TD className="text-right">
                  <Link
                    to={`/screen-flows/${f.id}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('screenFlows.open', 'Open')}
                  </Link>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
